import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { getCurrentAppPath } from '@/lib/auth-routing';
import { useSession } from '@/hooks/useSession';

function AuthLoadingState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-foreground">Metis</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }): React.ReactElement {
  const { hash, pathname, search } = useLocation();
  const { status, startLogin } = useSession();
  const redirectStartedRef = useRef(false);

  useEffect(() => {
    if (status !== 'unauthenticated' || redirectStartedRef.current) {
      return;
    }

    redirectStartedRef.current = true;
    startLogin(`${pathname}${search}${hash}` || getCurrentAppPath(window.location));
  }, [hash, pathname, search, startLogin, status]);

  if (status === 'loading') {
    return <AuthLoadingState message="Checking your session…" />;
  }

  if (status === 'error') {
    return (
      <AuthLoadingState message="Could not verify your session. Check the server and refresh to try again." />
    );
  }

  if (status === 'unauthenticated') {
    return <AuthLoadingState message="Redirecting to GitHub sign-in…" />;
  }

  return <>{children}</>;
}
